import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useSite } from '../../contexts/SiteContext';
import { useAuth } from '../../contexts/AuthContext';

interface TitleProps {
  isOpen?: boolean;
}

interface ListProps {
  isOpen?: boolean;
}

interface MobileDisabledProps {
  mobileDisabled?: boolean;
}

const FooterContainer = styled.footer`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.footerBg};
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.lg} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.spacing.sm};
  }
`;

const FooterTop = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const FooterSection = styled.div<MobileDisabledProps>`
  flex: 1;
  min-width: 200px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    min-width: 0;
    padding: 0 ${({ theme }) => theme.spacing.md};

    display: ${props => props.mobileDisabled ? 'none' : 'block'};
  }
`;

const FooterTitle = styled.h3<TitleProps>`
  font-size: ${({ theme }) => theme.fontSizes.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 0;
    padding: ${({ theme }) => theme.spacing.sm} 0;
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    
    &::after {
      content: "${props => props.isOpen ? '−' : '+'}";
      font-size: 1.25rem;
      font-weight: 400;
    }
  }
`;

const FooterList = styled.ul<ListProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: center;
    display: ${props => props.isOpen ? 'flex' : 'none'};
    padding-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TelegramIcon = styled.span`
  font-size: 1rem;
  color: #0088cc;
`;

const FooterText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.secondary};
`;

const FooterLogo = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 800;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.info});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;
  width: 100%;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: ${({ theme }) => theme.spacing.sm};
    padding-top: ${({ theme }) => theme.spacing.sm};
  }
`;

const FooterBottom = styled.div<MobileDisabledProps>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.sm};

    display: ${props => props.mobileDisabled ? 'none' : 'flex'};
  }
`;

const FooterBottomLink = styled(Link)`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const MobileFooterInfo = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    color: ${({ theme }) => theme.colors.secondary};
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

type SectionKey = 'community' | 'member' | 'support';

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const { siteConfig } = useSite();
  const { isAuthenticated, user } = useAuth();
  const [openSections, setOpenSections] = useState({
    community: false,
    member: false,
    support: false
  });

  const toggleSection = (section: SectionKey) => {
    if (window.innerWidth <= 768) { // 모바일 환경에서만 토글 적용
      setOpenSections({
        ...openSections,
        [section]: !openSections[section]
      });
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <FooterSection>
            <FooterLogo>{siteConfig.siteName}</FooterLogo>
            <FooterText>안전하고 즐거운 커뮤니티 플랫폼</FooterText>
            <FooterText>24시간 고객센터 운영</FooterText>
          </FooterSection>
          
          <FooterSection mobileDisabled={true}>
            <FooterTitle 
              onClick={() => toggleSection('community')}
              isOpen={openSections.community}
            >
              커뮤니티
            </FooterTitle>
            <FooterList isOpen={openSections.community}>
              <li><FooterLink to="/">홈</FooterLink></li>
              <li><FooterLink to="/boards">게시판 목록</FooterLink></li>
              <li><FooterLink to="/boards/1">공지사항</FooterLink></li>
              <li><FooterLink to="/boards/2">자유게시판</FooterLink></li>
            </FooterList>
          </FooterSection>
          
          <FooterSection mobileDisabled={true}>
            <FooterTitle 
              onClick={() => toggleSection('member')}
              isOpen={openSections.member}
            >
              {isAuthenticated && user ? '마이페이지' : '회원'}
            </FooterTitle>
            <FooterList isOpen={openSections.member}>
              {isAuthenticated && user ? (
                <>
                  <li><FooterLink to="/mypage/info">내 정보</FooterLink></li>
                  <li><FooterLink to="/mypage/posts">내 게시글</FooterLink></li>
                  <li><FooterLink to="/mypage/activities">활동내역</FooterLink></li>
                  <li><FooterLink to="/mypage/points">포인트 관리</FooterLink></li>
                </>
              ) : (
                <>
                  <li><FooterLink to="/login">로그인</FooterLink></li>
                  <li><FooterLink to="/register">회원가입</FooterLink></li>
                </>
              )}
            </FooterList>
          </FooterSection>
          
          <FooterSection mobileDisabled={true}>
            <FooterTitle 
              onClick={() => toggleSection('support')}
              isOpen={openSections.support}
            >
              고객지원
            </FooterTitle>
            <FooterList isOpen={openSections.support}>
              <li><FooterLink to="/inquiry">문의하기</FooterLink></li>
              <li><SocialLink href="https://t.me/d" target="_blank" rel="noopener noreferrer">
                텔레그램 문의 <TelegramIcon>📱</TelegramIcon>
              </SocialLink></li>
            </FooterList>
          </FooterSection>
        </FooterTop>
        
        <MobileFooterInfo>
          <span>{siteConfig.copyright}</span>
          <span>안전하고 즐거운 커뮤니티 플랫폼</span>
          <span>문의: support@example.com</span>
        </MobileFooterInfo>
        
        <FooterBottom mobileDisabled={true}>
          <FooterBottomLink to="/terms">이용약관</FooterBottomLink>
          <FooterBottomLink to="/privacy">개인정보처리방침</FooterBottomLink>
          <FooterBottomLink to="/cookie">쿠키정책</FooterBottomLink>
          <FooterBottomLink to="/contact">고객센터</FooterBottomLink>
        </FooterBottom>
        
        <Copyright>{siteConfig.copyright}</Copyright>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 